import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { Plus, X, Pencil, Trash2, Search, Save, MapPin, UserCheck, Building2, CheckCircle2, Eye, Phone, Mail, Landmark, HardHat, Tag, Paperclip, FileText, Image as ImageIcon, Upload, Download, AlertTriangle, ShieldAlert } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { BranchSelect } from "@/components/BranchSelect";
import { BranchFilter } from "@/components/BranchFilter";
import { BulkUploadButtons, BulkUploadResultBanner } from "@/components/BulkUploadControls";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { PaginationControls } from "@/components/PaginationControls";
import { useFormTab } from "@/hooks/useFormTab";
import { ActiveStatusCell, ActiveStatusFilter, matchActiveFilter } from "@/components/ActiveStatusCell";

const blank = () => ({
  vendor_code: "", name: "", branch_id: "", branch_name: "", contact_person: "", phone: "", email: "",
  additional_emails: [],
  address: "", gst_number: "", gst_verified: false, gst_verified_at: "",
  pan: "", cin: "", aadhaar_number: "", license_number: "",
  skill_type: "",
  payment_terms: "", tds_section: "194C", tds_rate: 1.0,
  billing_address: "", bank_details: "",
  bank_accounts: [],
  pocs: [],
  attachments: [],
  opening_balance: 0,
  is_active: true,
});

const MAX_ATTACHMENTS = 3;
const MAX_ATTACH_BYTES = 1024 * 1024; // 1 MB
const ALLOWED_EXTS = [".pdf", ".jpg", ".jpeg", ".png"];

function fmtBytes(b) {
  if (!b) return "0 KB";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

function backendUrl(rel) {
  if (!rel) return "";
  if (rel.startsWith("http")) return rel;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${rel}`;
}

function VendorApprovalBadge({ status }) {
  const s = (status || "Approved");  // legacy rows w/o field
  const map = {
    Approved: "bg-emerald-100 text-emerald-800",
    Pending:  "bg-amber-100 text-amber-800",
    Draft:    "bg-slate-200 text-slate-700",
    Rejected: "bg-rose-100 text-rose-800",
  };
  return (
    <span data-testid={`vendor-approval-${s.toLowerCase()}`}
      className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-bold ${map[s] || map.Draft}`}>
      {s}
    </span>
  );
}

export default function VendorsPage() {
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [filterActive, setFilterActive] = useState("active");
  const { active: activeBranch } = useBranch();

  // Sprint 2026-Feb — Server-side search. The prior code filtered the
  // ALREADY-PAGINATED page's 25 rows locally, meaning a user searching
  // for a vendor that lived on page 8 saw "no results" and had to
  // manually click the next-page arrow eight times to reach it. The
  // backend `/vendors` endpoint already accepts a `q` query param
  // (case-insensitive regex OR-match across vendor_code / name /
  // contact_person / email / phone / gst_number / pan). We debounce
  // the input by 300ms so we don't fire a server round-trip on every
  // keystroke — reduces load on the 1300-vendor collection.
  const debouncedSearch = useDebouncedValue(search, 300);
  const params = useMemo(() => {
    const p = {};
    if (filterBranch) p.branch_id = filterBranch;
    if (debouncedSearch.trim()) p.q = debouncedSearch.trim();
    return p;
  }, [filterBranch, debouncedSearch]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/vendors", params);
  const load = reload;
  const openFormTab = useFormTab();

  const openNew = () => openFormTab({
    path: "/vendors/new",
    label: "New Vendor",
    icon: Plus,
    parent: "/vendors",
    onSaved: load,
    render: ({ close, saved, viewList }) => (
      <VendorForm record={blank()} activeBranch={activeBranch}
        onClose={close} onSaved={saved} onViewList={viewList} />
    ),
  });
  const openEdit = (v) => {
    // Sprint 4.11.17 — Editing an already-Approved vendor will reset it to
    // Draft and re-route through the approval matrix. Warn the user up
    // front so they understand transactions against this vendor will be
    // blocked until re-approval is granted.
    if (v.approval_status === "Approved") {
      const ok = window.confirm(
        `Editing this vendor will reset its approval status.\n\n` +
        `"${v.name}" (${v.vendor_code}) is currently Approved. After you save your changes:\n` +
        `  • Its status becomes "Draft" and is re-routed through the matrix.\n` +
        `  • No new transaction can use it until the approver re-approves.\n` +
        `  • Existing transactions are unaffected.\n\n` +
        `Continue?`,
      );
      if (!ok) return;
    }
    openFormTab({
      path: `/vendors/edit/${v.id}`,
      label: `Edit · ${v.vendor_code || v.vendor_name || "Vendor"}`,
      icon: Pencil,
      parent: "/vendors",
      onSaved: load,
      render: ({ close, saved, viewList }) => (
        <VendorForm record={v} activeBranch={activeBranch}
          onClose={close} onSaved={saved} onViewList={viewList} />
      ),
    });
  };
  const openView = (v) => openFormTab({
    path: `/vendors/view/${v.id}`,
    label: `View · ${v.vendor_code || v.vendor_name || "Vendor"}`,
    icon: Eye,
    parent: "/vendors",
    render: ({ close }) => <VendorView record={v} onClose={close} onEdit={() => { close(); openEdit(v); }} />,
  });

  async function del(id, name) {
    if (!window.confirm(`Delete vendor "${name}"?`)) return;
    await api.delete(`/vendors/${id}`);
    toast.success("Deleted"); load();
  }

  // Server-side handles branch_id + `q` search now — we only apply the
  // active/inactive UI toggle client-side (it's a per-user preference,
  // not a persistent DB filter).
  const filtered = list.filter(c => matchActiveFilter(c, filterActive));

  return (
    <div data-testid="vendors-page">
      <Toaster richColors position="top-right" />
      <PageHeader eyebrow="Master" title="Vendors"
        subtitle="Vendor master with skill, statutory codes, multiple Points of Contact, TDS defaults and multiple bank accounts. Bank accounts are pickable on Purchase Invoices & Payments."
        onRefresh={load}
        refreshing={loading}
        actions={
          <>
            <BulkUploadButtons master="vendors" filename="vendors_master_template.xlsx" onUploaded={load} />
            <button data-testid="add-vendor-btn" onClick={openNew}
                    className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Plus size={14} /> Add Vendor
            </button>
          </>
        }
      />

      <BulkUploadResultBanner master="vendors" />

      <div className="relative mb-4 flex items-center gap-3 max-w-3xl">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${loading && search ? "text-[#F97316] animate-pulse" : "text-slate-400"}`} />
          <input value={search} onChange={e => setSearch(e.target.value)}
                 placeholder="Search across all vendors — code, name, GST, PAN, email, phone…"
                 data-testid="vendor-search-input"
                 className="w-full pl-9 pr-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none" />
        </div>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="vendors-filter-branch" />
        <ActiveStatusFilter value={filterActive} onChange={setFilterActive} testid="vendors-filter-active" />
        {search && (
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold" data-testid="vendor-search-result-count">
            {loading ? "…" : `${total} match${total === 1 ? "" : "es"}`}
          </span>
        )}
      </div>

      <Card className="overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>{["Code", "Name", "Vendor Tag", "GST", "PAN", "POCs", "Banks", "TDS %", "Op. Balance", "Approval", "Status", ""].map(h =>
              <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
            )}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="p-10 text-center text-slate-500 text-sm">No vendors yet.</td></tr>
            )}
            {filtered.map(c => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="py-3 px-4 text-xs font-bold">{c.vendor_code}</td>
                <td className="py-3 px-4 text-sm">{c.name}</td>
                <td className="py-3 px-4 text-xs">{c.skill_type || "—"}</td>
                <td className="py-3 px-4 text-xs">
                  {c.gst_number || "—"} {c.gst_verified && <CheckCircle2 size={12} className="inline text-emerald-600 ml-1" />}
                </td>
                <td className="py-3 px-4 text-xs">{c.pan || "—"}</td>
                <td className="py-3 px-4 text-xs">{(c.pocs || []).length}</td>
                <td className="py-3 px-4 text-xs">{(c.bank_accounts || []).length}</td>
                <td className="py-3 px-4 text-xs tabular">{(c.tds_rate ?? 1).toFixed(2)}</td>
                <td className="py-3 px-4 text-sm tabular">{formatINR(c.opening_balance || 0)}</td>
                <td className="py-3 px-4">
                  <VendorApprovalBadge status={c.approval_status} />
                </td>
                <td className="py-3 px-4">
                  <ActiveStatusCell row={c} resource="vendors" onChanged={load} />
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`view-vendor-${c.id}`} onClick={() => openView(c)} className="text-slate-500 hover:text-blue-600 p-1" title="View"><Eye size={14} /></button>
                    <button data-testid={`edit-vendor-${c.id}`} onClick={() => openEdit(c)} className="text-slate-500 hover:text-[#F97316] p-1" title="Edit"><Pencil size={14} /></button>
                    <button data-testid={`delete-vendor-${c.id}`} onClick={() => del(c.id, c.name)} className="text-slate-500 hover:text-red-600 p-1" title="Delete"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
      </Card>
    </div>
  );
}

function VendorForm({ record, activeBranch, onClose, onSaved, onViewList }) {
  const isEdit = !!record?.id;
  const seedForm = () => ({ ...blank(), branch_id: activeBranch?.id || "", branch_name: activeBranch?.branch_name || "", ...record });
  const [form, setForm] = useState(seedForm);
  const [saving, setSaving] = useState(false);
  // After a successful CREATE, surface a persistent green banner above the
  // form (in addition to the toast) so the user has unambiguous confirmation
  // and can immediately keep adding more vendors in the SAME tab without the
  // form vanishing — matching the pattern used by IssueVoucherForm.
  const [lastSaved, setLastSaved] = useState(null); // { vendor_code, name }
  const [tags, setTags] = useState([]);
  const [addingTag, setAddingTag] = useState(false);
  const [newTag, setNewTag] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });
  const [emailDraft, setEmailDraft] = useState("");
  const addExtraEmail = () => {
    const em = emailDraft.trim().toLowerCase();
    if (!em) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { toast.error("Enter a valid email address"); return; }
    const list = form.additional_emails || [];
    if (list.some(x => (x || "").toLowerCase() === em) || (form.email || "").trim().toLowerCase() === em) {
      toast.error("Email already added"); return;
    }
    setForm({ ...form, additional_emails: [...list, em] });
    setEmailDraft("");
  };
  const { hasPerm } = useAuth();
  // Vendor Code is auto-generated on save, but users with `vendors.edit`
  // permission can override the suggested code at creation time OR change it
  // later from the Edit view. Read-only viewers still see it locked.
  const canEditVendorCode = hasPerm("vendors", "edit") || hasPerm("vendors", "create");
  // Tracks whether the user has explicitly edited the field — once edited we
  // STOP auto-suggesting the next code so we don't clobber their manual entry.
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(Boolean((form.vendor_code || "").trim()));

  useEffect(() => { api.get("/vendor-tags").then(r => setTags(r.data || [])); }, []);

  async function createTag() {
    const name = newTag.trim();
    if (!name) { toast.error("Tag name is required"); return; }
    if (tags.some(t => (t.name || "").toLowerCase() === name.toLowerCase())) {
      toast.error("Tag already exists"); return;
    }
    try {
      const r = await api.post("/vendor-tags", { name });
      const created = r.data;
      setTags([...tags, created]);
      set("skill_type", created.name);
      setNewTag(""); setAddingTag(false);
      toast.success("Tag added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add tag");
    }
  }

  function collapseAll(arr) { return (arr || []).map(x => ({ ...x, _editing: false })); }

  function addPoc() {
    const collapsed = collapseAll(form.pocs);
    setForm({ ...form, pocs: [...collapsed, { name: "", designation: "", phone: "", email: "", notes: "", _editing: true }] });
  }
  function setPoc(i, k, v) { const arr = [...(form.pocs || [])]; arr[i] = { ...arr[i], [k]: v }; setForm({ ...form, pocs: arr }); }
  function delPoc(i) { const arr = [...(form.pocs || [])]; arr.splice(i, 1); setForm({ ...form, pocs: arr }); }
  function editPoc(i) { const arr = collapseAll(form.pocs); arr[i] = { ...arr[i], _editing: true }; setForm({ ...form, pocs: arr }); }
  function savePoc(i) {
    const arr = [...(form.pocs || [])];
    if (!arr[i].name?.trim()) { toast.error("POC name is required"); return false; }
    arr[i] = { ...arr[i], _editing: false }; setForm({ ...form, pocs: arr }); return true;
  }
  function savePocAndAdd(i) { if (savePoc(i)) addPoc(); }

  // Attachment helpers
  const [uploadingAttach, setUploadingAttach] = useState(false);
  async function onPickFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file later
    if (!file) return;
    if ((form.attachments || []).length >= MAX_ATTACHMENTS) {
      toast.error(`Max ${MAX_ATTACHMENTS} attachments allowed`); return;
    }
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      toast.error(`Only ${ALLOWED_EXTS.join(", ")} allowed`); return;
    }
    if (file.size > MAX_ATTACH_BYTES) {
      toast.error(`File too large (${fmtBytes(file.size)}). Max 1 MB per attachment.`); return;
    }
    setUploadingAttach(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/vendor-attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, attachments: [...(f.attachments || []), r.data] }));
      toast.success("Attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploadingAttach(false); }
  }
  function removeAttach(i) {
    const arr = [...(form.attachments || [])]; arr.splice(i, 1);
    setForm({ ...form, attachments: arr });
  }

  function addBank() {
    const collapsed = collapseAll(form.bank_accounts);
    setForm({ ...form, bank_accounts: [...collapsed, { label: "", account_holder_name: form.name || "", bank_name: "", account_number: "", ifsc_code: "", branch: "", account_type: "Current", upi_id: "", is_default: collapsed.length === 0, _editing: true }] });
  }
  function setBank(i, k, v) { const arr = [...(form.bank_accounts || [])]; arr[i] = { ...arr[i], [k]: v }; setForm({ ...form, bank_accounts: arr }); }
  function delBank(i) { const arr = [...(form.bank_accounts || [])]; arr.splice(i, 1); setForm({ ...form, bank_accounts: arr }); }
  function editBank(i) { const arr = collapseAll(form.bank_accounts); arr[i] = { ...arr[i], _editing: true }; setForm({ ...form, bank_accounts: arr }); }
  function makeDefaultBank(i) { const arr = (form.bank_accounts || []).map((b, j) => ({ ...b, is_default: j === i })); setForm({ ...form, bank_accounts: arr }); }
  function saveBank(i) {
    const arr = [...(form.bank_accounts || [])];
    if (!arr[i].bank_name?.trim()) { toast.error("Bank Name is required"); return false; }
    if (!arr[i].account_number?.trim()) { toast.error("Account Number is required"); return false; }
    if (!arr[i].ifsc_code?.trim()) { toast.error("IFSC Code is required"); return false; }
    arr[i] = { ...arr[i], _editing: false }; setForm({ ...form, bank_accounts: arr }); return true;
  }
  function saveBankAndAdd(i) { if (saveBank(i)) addBank(); }

  async function verifyGst() {
    if (!form.gst_number) { toast.error("Enter a GST Number first"); return; }
    set("gst_verified", true); set("gst_verified_at", new Date().toISOString());
    toast.success("GST marked verified (portal integration coming soon)");
  }

  // For new vendors, preview the auto-generated code so the user sees what the
  // backend will assign on save. Existing vendors keep their original code.
  // We do NOT overwrite once the user has manually typed something (tracked
  // by codeManuallyEdited) so admin overrides survive a re-render.
  useEffect(() => {
    if (form.id) return;
    if (codeManuallyEdited) return;
    if ((form.vendor_code || "").trim()) return;
    api.get("/vendors/_next-code").then(r => set("vendor_code", r.data?.vendor_code || ""))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id, codeManuallyEdited]);

  // Sprint 4.11.16 — Possible-duplicate modal state. When the backend returns
  // a 409 with code=duplicate_vendor_warning we surface this modal so the user
  // can review the matching vendor(s) and either cancel, open the existing
  // record, or continue with a reason (gated by `vendors.override_duplicate`).
  const [dupWarn, setDupWarn] = useState(null); // { matched_vendors, warning_fields, risk }
  const [dupBlocked, setDupBlocked] = useState(null); // { matched_vendors, blocking_fields, message }

  async function save(e) {
    e.preventDefault();
    if (!form.name) { toast.error("Name is required"); return; }
    if (!form.branch_id) { toast.error("Branch is mandatory"); return; }
    await runSave({ overrideReason: "" });
  }

  async function runSave({ overrideReason }) {
    setSaving(true);
    try {
      const payload = {
        ...form,
        pocs: (form.pocs || []).map(({ _editing, ...rest }) => rest),
        bank_accounts: (form.bank_accounts || []).map(({ _editing, ...rest }) => rest),
      };
      const params = overrideReason ? `?duplicate_override_reason=${encodeURIComponent(overrideReason)}` : "";
      if (isEdit) {
        await api.put(`/vendors/${form.id}${params}`, payload);
        toast.success("Updated");
        if (typeof onSaved === "function") onSaved();
        onClose();
      } else {
        const r = await api.post(`/vendors${params}`, payload);
        const saved = r.data || {};
        toast.success(`✓ Vendor ${saved.vendor_code || saved.name || ""} created`);
        setLastSaved({
          vendor_code: saved.vendor_code || payload.vendor_code || "",
          name: saved.name || payload.name || "",
          id: saved.id || "",
        });
        // Reset form for next entry (Sprint 4.11.14 stay-open-after-save UX)
        setForm({
          ...blank(),
          branch_id: form.branch_id,
          branch_name: form.branch_name,
        });
        setCodeManuallyEdited(false);
        setDupWarn(null);
        setDupBlocked(null);
        if (typeof onSaved === "function") onSaved();
      }
    } catch (err) {
      // Surface the duplicate-vendor responses via the in-form modal so the
      // user has full context (matched vendor list) — NOT a one-line toast.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === "object") {
        if (detail.code === "duplicate_vendor_blocked") {
          setDupBlocked({
            message: detail.message || "A duplicate vendor exists and cannot be saved.",
            blocking_fields: detail.blocking_fields || [],
            matched_vendors: detail.matched_vendors || [],
          });
        } else if (detail.code === "duplicate_vendor_warning") {
          setDupWarn({
            message: detail.message || "Possible duplicate vendor found.",
            warning_fields: detail.warning_fields || [],
            matched_vendors: detail.matched_vendors || [],
            risk: detail.risk || "low",
          });
        } else {
          toast.error(detail.message || JSON.stringify(detail));
        }
      } else if (err.response?.status === 403 && detail && typeof detail === "object" && detail.code === "duplicate_override_forbidden") {
        toast.error(detail.message || "Override not permitted");
        setDupWarn(null);
      } else {
        toast.error(detail || "Save failed");
      }
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="vendor-form-overlay">
      <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
        <h2 className="font-display text-2xl font-bold">{isEdit ? "Edit" : "New"} Vendor</h2>
        <div className="flex items-center gap-3">
          <button data-testid="save-vendor-btn" form="vendor-form" type="submit" disabled={saving} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Save size={14} /> {saving ? "Saving…" : "Save Vendor"}
          </button>
          {onViewList && (
            <button type="button" data-testid="vendor-view-list-btn" onClick={onViewList}
              className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">
              View List
            </button>
          )}
          <button onClick={onClose} className="p-2 hover:bg-slate-100" title="Close"><X size={20} /></button>
        </div>
      </div>

      <form id="vendor-form" onSubmit={save} className="px-8 py-6 space-y-5 max-w-[1400px] mx-auto">
          {/* Persistent saved confirmation — survives until the user dismisses
              or starts editing line items again. Eliminates the "did my save
              go through?" doubt and keeps the form open so the next vendor
              entry happens in the SAME tab (no re-navigation needed). */}
          {lastSaved && !isEdit && (
            <div className="bg-emerald-50 border-2 border-emerald-400 px-4 py-3 flex items-start gap-3"
                 data-testid="vendor-saved-banner">
              <CheckCircle2 size={20} className="text-emerald-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-bold text-emerald-900">
                  ✓ Vendor created &nbsp;
                  <span className="font-mono text-emerald-700">{lastSaved.vendor_code || lastSaved.name}</span>
                </div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  Form has been cleared for the next entry. Click <em>View List</em> to see it, or close (X) to exit.
                </div>
              </div>
              <button type="button"
                      data-testid="vendor-saved-banner-dismiss"
                      onClick={() => setLastSaved(null)}
                      className="text-emerald-700 hover:text-emerald-900 p-1"
                      title="Dismiss confirmation">
                <X size={14} />
              </button>
            </div>
          )}          {/* Identity */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Building2 size={14} className="text-slate-500" />
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Identity &amp; Statutory</h3>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Branch *">
                <BranchSelect value={form.branch_id} onChange={(id, name) => setForm({ ...form, branch_id: id, branch_name: name })} testid="vendor-branch-select" />
              </Field>
              <Field
                label={form.id ? "Vendor Code" : "Vendor Code (auto · editable)"}
                hint={canEditVendorCode ? "Override the suggested code by typing your own. Must remain unique across vendors." : "Auto-generated on save."}
              >
                <input
                  data-testid="vendor-code"
                  value={form.vendor_code}
                  readOnly={!canEditVendorCode}
                  onChange={(e) => {
                    if (!canEditVendorCode) return;
                    setCodeManuallyEdited(true);
                    set("vendor_code", e.target.value);
                  }}
                  placeholder="Will be auto-generated"
                  className={`${inputCls} ${canEditVendorCode ? "" : "bg-slate-50 text-slate-700 cursor-not-allowed"}`}
                />
              </Field>
              <Field label="Name *"><input data-testid="vendor-name" value={form.name} onChange={e => set("name", e.target.value)} className={inputCls} /></Field>
              <Field label="Vendor Tag">
                {addingTag ? (
                  <div className="flex gap-2">
                    <input autoFocus value={newTag} onChange={e => setNewTag(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); createTag(); } if (e.key === "Escape") { setAddingTag(false); setNewTag(""); } }}
                      placeholder="Type new tag name…" className={inputCls + " flex-1"} />
                    <button type="button" data-testid="confirm-add-tag" onClick={createTag} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 text-[11px] uppercase tracking-wider font-bold whitespace-nowrap">Add</button>
                    <button type="button" onClick={() => { setAddingTag(false); setNewTag(""); }} className="px-2 py-2 text-slate-500 hover:text-slate-800"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <select data-testid="vendor-tag" value={form.skill_type || ""} onChange={e => set("skill_type", e.target.value)} className={inputCls + " flex-1"}>
                      <option value="">— Select tag —</option>
                      {tags.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                      {/* If the vendor was saved with a tag that no longer
                       * exists in the /vendor-tags library (legacy free-text,
                       * deleted tag, or tag created in another session), the
                       * <select> would otherwise render BLANK because no
                       * <option> matches `form.skill_type`. Inject it as a
                       * fallback option so the value displays correctly and
                       * is preserved on save. */}
                      {form.skill_type && !tags.some(t => (t.name || "") === form.skill_type) && (
                        <option key="__legacy__" value={form.skill_type}>{form.skill_type} (legacy)</option>
                      )}
                    </select>
                    <button type="button" data-testid="add-tag-btn" onClick={() => setAddingTag(true)} className="border border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white px-3 py-2 text-[11px] uppercase tracking-wider font-bold whitespace-nowrap flex items-center gap-1.5">
                      <Plus size={12} /> New Tag
                    </button>
                  </div>
                )}
              </Field>
              <Field label="Phone"><input value={form.phone || ""} onChange={e => set("phone", e.target.value)} className={inputCls} /></Field>
              <Field label="Email"><input type="email" value={form.email || ""} onChange={e => set("email", e.target.value)} className={inputCls} /></Field>
              <div className="md:col-span-2">
                <Field label="Additional Emails">
                  <div className="flex gap-2">
                    <input type="email" data-testid="vendor-additional-email-input" value={emailDraft}
                      onChange={e => setEmailDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addExtraEmail(); } }}
                      placeholder="Type an email and press Enter / Add"
                      className={inputCls + " flex-1"} />
                    <button type="button" data-testid="vendor-additional-email-add-btn" onClick={addExtraEmail}
                      className="border border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white px-3 py-2 text-[11px] uppercase tracking-wider font-bold whitespace-nowrap flex items-center gap-1.5">
                      <Plus size={12} /> Add
                    </button>
                  </div>
                  {(form.additional_emails || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="vendor-additional-email-chips">
                      {(form.additional_emails || []).map((em, i) => (
                        <span key={i} data-testid={`vendor-additional-email-chip-${i}`}
                          className="inline-flex items-center gap-1.5 border border-slate-300 bg-slate-50 px-2 py-1 text-[11px] font-mono text-slate-700">
                          {em}
                          <button type="button" data-testid={`vendor-additional-email-remove-${i}`}
                            onClick={() => set("additional_emails", (form.additional_emails || []).filter((_, j) => j !== i))}
                            className="text-rose-600 font-bold hover:text-rose-800">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </Field>
              </div>
              <Field label="Primary Contact (legacy)"><input value={form.contact_person || ""} onChange={e => set("contact_person", e.target.value)} className={inputCls} placeholder="(See POCs below for the new way)" /></Field>
              <div className="md:col-span-2">
                <Field label="GST Number">
                  <div className="flex gap-2">
                    <input value={form.gst_number || ""} onChange={e => set("gst_number", e.target.value.toUpperCase())} placeholder="07AAACR1234A1Z5" className={inputCls + " flex-1"} />
                    <button type="button" onClick={verifyGst} className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 text-[11px] uppercase tracking-wider font-bold whitespace-nowrap flex items-center gap-1.5">
                      <CheckCircle2 size={12} /> Verify
                    </button>
                  </div>
                  {form.gst_verified && (<div className="text-[10px] text-emerald-700 mt-1">Verified {form.gst_verified_at ? `· ${new Date(form.gst_verified_at).toLocaleDateString()}` : ""}</div>)}
                </Field>
              </div>
              <Field label="PAN Number"><input value={form.pan || ""} onChange={e => set("pan", e.target.value.toUpperCase())} placeholder="AAACR1234A" className={inputCls} /></Field>
              <Field label="CIN (if Pvt/Ltd)"><input value={form.cin || ""} onChange={e => set("cin", e.target.value.toUpperCase())} className={inputCls} /></Field>
              <Field label="Aadhaar (proprietor)"><input value={form.aadhaar_number || ""} onChange={e => set("aadhaar_number", e.target.value)} className={inputCls} /></Field>
              <Field label="Vendor License #"><input value={form.license_number || ""} onChange={e => set("license_number", e.target.value)} className={inputCls} /></Field>
              <Field label="TDS Section">
                <select value={form.tds_section || "194C"} onChange={e => set("tds_section", e.target.value)} className={inputCls}>
                  <option>194C</option><option>194J</option><option>194I</option><option>None</option>
                </select>
              </Field>
              <Field label="TDS Rate (%)"><input type="number" step="0.01" value={form.tds_rate ?? 1} onChange={e => set("tds_rate", Number(e.target.value))} className={inputCls} /></Field>
              <Field label="Payment Terms"><input value={form.payment_terms || ""} onChange={e => set("payment_terms", e.target.value)} placeholder="Net 30 / Running bill" className={inputCls} /></Field>
              <Field label="Opening Balance (₹)"><input type="number" step="0.01" value={form.opening_balance || 0} onChange={e => set("opening_balance", Number(e.target.value))} className={inputCls} /></Field>
              <Field label="Status">
                <label className="flex items-center gap-2 text-sm pt-2" data-testid="vendor-active-toggle">
                  <input type="checkbox" checked={form.is_active !== false} onChange={e => set("is_active", e.target.checked)}
                         className="w-4 h-4 accent-[#F97316]" />
                  <span className={form.is_active !== false ? "text-emerald-700 font-bold" : "text-slate-500"}>
                    {form.is_active !== false ? "Active" : "Inactive (hidden from transaction dropdowns)"}
                  </span>
                </label>
              </Field>
              <div className="md:col-span-3">
                <Field label="Registered Address"><textarea rows={2} value={form.address || ""} onChange={e => set("address", e.target.value)} className={inputCls} /></Field>
              </div>
            </div>
          </Card>

          {/* Billing */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <MapPin size={14} className="text-slate-500" />
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Billing Address</h3>
            </div>
            <div className="p-5">
              <textarea rows={3} value={form.billing_address || ""} onChange={e => set("billing_address", e.target.value)} placeholder="Default billing address used on Purchase Invoices" className={inputCls} />
            </div>
          </Card>

          {/* Bank Accounts */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Landmark size={14} className="text-slate-500" />
                <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Bank Accounts</h3>
                <span className="text-[10px] text-slate-400">— pickable on Purchase Invoices &amp; Payments</span>
              </div>
              <button type="button" data-testid="add-bank-btn" onClick={addBank} className="text-[10px] font-bold uppercase tracking-wider text-[#F97316] hover:underline flex items-center gap-1"><Plus size={12} /> Add Bank Account</button>
            </div>
            <div className="p-5 space-y-4">
              {(form.bank_accounts || []).length === 0 && (
                <div className="text-center py-6 text-xs text-slate-500">No bank accounts yet. Click <strong>Add Bank Account</strong>.</div>
              )}
              {(form.bank_accounts || []).map((b, i) => b._editing ? (
                <div key={i} className="border border-slate-200 p-4 bg-slate-50/30" data-testid={`bank-row-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <input value={b.label || ""} onChange={e => setBank(i, "label", e.target.value)} placeholder="Label (e.g. SBI Savings — Site Account)" className={`${inputCls} font-bold max-w-md`} />
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 text-[11px]">
                        <input type="radio" name="default_bank" checked={!!b.is_default} onChange={() => makeDefaultBank(i)} /> Default
                      </label>
                      <button type="button" onClick={() => delBank(i)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input value={b.account_holder_name || ""} onChange={e => setBank(i, "account_holder_name", e.target.value)} placeholder="Account Holder Name *" className={inputCls} />
                    <input value={b.bank_name || ""} onChange={e => setBank(i, "bank_name", e.target.value)} placeholder="Bank Name *" className={inputCls} />
                    <select value={b.account_type || "Current"} onChange={e => setBank(i, "account_type", e.target.value)} className={inputCls}>
                      <option>Current</option><option>Savings</option><option>OD</option><option>CC</option>
                    </select>
                    <input value={b.account_number || ""} onChange={e => setBank(i, "account_number", e.target.value)} placeholder="Account Number *" className={inputCls} />
                    <input value={b.ifsc_code || ""} onChange={e => setBank(i, "ifsc_code", e.target.value.toUpperCase())} placeholder="IFSC Code *" className={inputCls} />
                    <input value={b.branch || ""} onChange={e => setBank(i, "branch", e.target.value)} placeholder="Branch" className={inputCls} />
                    <input value={b.upi_id || ""} onChange={e => setBank(i, "upi_id", e.target.value)} placeholder="UPI ID (optional)" className={inputCls} />
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-200">
                    <button type="button" data-testid={`save-bank-${i}`} onClick={() => saveBank(i)} className="bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"><Save size={12} /> Save</button>
                    <button type="button" data-testid={`save-add-bank-${i}`} onClick={() => saveBankAndAdd(i)} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"><Plus size={12} /> Save &amp; Add Another</button>
                  </div>
                </div>
              ) : (
                <div key={i} className="border border-slate-200 px-4 py-3 bg-white flex items-center justify-between hover:bg-slate-50/60" data-testid={`bank-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm">{b.label || b.bank_name || "(no label)"}</span>
                      {b.is_default && <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-bold">Default</span>}
                      <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 font-bold">{b.account_type}</span>
                    </div>
                    <div className="text-xs text-slate-600 truncate mt-0.5 tabular">
                      {b.bank_name} · A/c {b.account_number} · IFSC {b.ifsc_code} {b.branch && `· ${b.branch}`}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{b.account_holder_name} {b.upi_id && `· UPI ${b.upi_id}`}</div>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <label className="flex items-center gap-1.5 text-[11px]">
                      <input type="radio" name="default_bank" checked={!!b.is_default} onChange={() => makeDefaultBank(i)} /> Default
                    </label>
                    <button type="button" onClick={() => editBank(i)} className="text-slate-500 hover:text-[#F97316] p-1"><Pencil size={14} /></button>
                    <button type="button" onClick={() => delBank(i)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* POCs */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck size={14} className="text-slate-500" />
                <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Points of Contact (POCs)</h3>
              </div>
              <button type="button" data-testid="add-poc-btn" onClick={addPoc} className="text-[10px] font-bold uppercase tracking-wider text-[#F97316] hover:underline flex items-center gap-1"><Plus size={12} /> Add POC</button>
            </div>
            <div className="p-5 space-y-3">
              {(form.pocs || []).length === 0 && (
                <div className="text-center py-6 text-xs text-slate-500">No POCs yet.</div>
              )}
              {(form.pocs || []).map((p, i) => p._editing ? (
                <div key={i} className="border border-slate-200 p-4 bg-slate-50/30" data-testid={`poc-row-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                    <input value={p.name || ""} onChange={e => setPoc(i, "name", e.target.value)} placeholder="Name *" className={`${inputCls} md:col-span-3`} />
                    <input value={p.designation || ""} onChange={e => setPoc(i, "designation", e.target.value)} placeholder="Designation" className={`${inputCls} md:col-span-2`} />
                    <input value={p.phone || ""} onChange={e => setPoc(i, "phone", e.target.value)} placeholder="Phone" className={`${inputCls} md:col-span-2`} />
                    <input value={p.email || ""} onChange={e => setPoc(i, "email", e.target.value)} placeholder="Email" className={`${inputCls} md:col-span-3`} />
                    <input value={p.notes || ""} onChange={e => setPoc(i, "notes", e.target.value)} placeholder="Notes" className={`${inputCls} md:col-span-2`} />
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                    <button type="button" data-testid={`save-poc-${i}`} onClick={() => savePoc(i)} className="bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"><Save size={12} /> Save</button>
                    <button type="button" data-testid={`save-add-poc-${i}`} onClick={() => savePocAndAdd(i)} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"><Plus size={12} /> Save &amp; Add Another</button>
                    <button type="button" onClick={() => delPoc(i)} className="ml-auto text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
                  </div>
                </div>
              ) : (
                <div key={i} className="border border-slate-200 px-4 py-2.5 bg-white flex items-center justify-between hover:bg-slate-50/60" data-testid={`poc-row-${i}`}>
                  <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-12 gap-2 text-xs">
                    <div className="md:col-span-3 font-bold text-sm truncate">{p.name || "(no name)"}</div>
                    <div className="md:col-span-2 text-slate-600 truncate">{p.designation || "—"}</div>
                    <div className="md:col-span-2 text-slate-600 truncate">{p.phone || "—"}</div>
                    <div className="md:col-span-3 text-slate-600 truncate">{p.email || "—"}</div>
                    <div className="md:col-span-2 text-slate-400 truncate">{p.notes || ""}</div>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    <button type="button" onClick={() => editPoc(i)} className="text-slate-500 hover:text-[#F97316] p-1"><Pencil size={14} /></button>
                    <button type="button" onClick={() => delPoc(i)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Attachments — last section */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Paperclip size={14} className="text-slate-500" />
                <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Documents / Attachments</h3>
                <span className="text-[10px] text-slate-400">— PDF / JPG / PNG &nbsp;·&nbsp; max 1&nbsp;MB each &nbsp;·&nbsp; up to {MAX_ATTACHMENTS} files</span>
              </div>
              <span className="text-[11px] font-bold text-slate-600 tabular">
                {(form.attachments || []).length}/{MAX_ATTACHMENTS}
              </span>
            </div>
            <div className="p-5 space-y-3">
              {(form.attachments || []).length === 0 && (
                <div className="text-center py-6 text-xs text-slate-500">No attachments yet.</div>
              )}
              {(form.attachments || []).map((a, i) => {
                const isPdf = (a.mime_type || "").includes("pdf") || (a.filename || "").toLowerCase().endsWith(".pdf");
                return (
                  <div key={a.id || i} className="border border-slate-200 px-4 py-3 bg-white flex items-center justify-between" data-testid={`attach-row-${i}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 flex items-center justify-center ${isPdf ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"}`}>
                        {isPdf ? <FileText size={16} /> : <ImageIcon size={16} />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold truncate">{a.filename}</div>
                        <div className="text-[11px] text-slate-500 tabular">{fmtBytes(a.size)} {a.uploaded_at && `· ${new Date(a.uploaded_at).toLocaleDateString()}`}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <a href={backendUrl(a.url)} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-blue-600 p-1" title="View"><Download size={14} /></a>
                      <button type="button" onClick={() => removeAttach(i)} className="text-red-500 hover:text-red-700 p-1" title="Remove"><Trash2 size={14} /></button>
                    </div>
                  </div>
                );
              })}
              {(form.attachments || []).length < MAX_ATTACHMENTS && (
                <label className={`block border-2 border-dashed border-slate-300 hover:border-[#F97316] hover:bg-orange-50/30 px-4 py-6 cursor-pointer text-center transition-colors ${uploadingAttach ? "opacity-50 pointer-events-none" : ""}`}>
                  <Upload size={18} className="inline text-slate-500 mr-2" />
                  <span className="text-xs uppercase tracking-wider font-bold text-slate-600">
                    {uploadingAttach ? "Uploading…" : "Click to upload (PDF / JPG / PNG, ≤ 1 MB)"}
                  </span>
                  <input type="file" data-testid="attach-input" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={onPickFile} />
                </label>
              )}
            </div>
          </Card>
        </form>

        {dupBlocked && (
          <DuplicateBlockedModal
            payload={dupBlocked}
            onClose={() => setDupBlocked(null)}
          />
        )}
        {dupWarn && (
          <DuplicateWarningModal
            payload={dupWarn}
            canOverride={hasPerm("vendors", "approve") || hasPerm("vendors", "override_duplicate")}
            onCancel={() => setDupWarn(null)}
            onContinue={async (reason) => {
              setDupWarn(null);
              await runSave({ overrideReason: reason });
            }}
          />
        )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sprint 4.11.16 — Duplicate-vendor modals
// ────────────────────────────────────────────────────────────────────────────

const FIELD_LABELS = {
  gstin:        "Same GSTIN",
  bank_account: "Same Bank Account + IFSC",
  pan:          "Same PAN",
  mobile:       "Same Mobile Number",
  name:         "Similar Vendor Name",
  name_city:    "Similar Name + Same City",
};

function MatchedVendorRow({ v }) {
  return (
    <div className="border border-slate-200 px-4 py-3 bg-white" data-testid={`matched-vendor-${v.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm truncate">
            {v.name} {v.vendor_code && <span className="text-[10px] font-mono text-slate-500 ml-1">{v.vendor_code}</span>}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {v.branch_name && `${v.branch_name} · `}
            {v.is_active ? <span className="text-emerald-600">Active</span> : <span className="text-rose-600">Inactive</span>}
            {v.created_at && ` · created ${v.created_at.slice(0, 10)}`}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-[11px]">
            {v.gst_number && <div><span className="text-slate-500">GST:</span> <span className="font-mono">{v.gst_number}</span></div>}
            {v.pan && <div><span className="text-slate-500">PAN:</span> <span className="font-mono">{v.pan}</span></div>}
            {v.phone && <div><span className="text-slate-500">Mobile:</span> {v.phone}</div>}
            {v.bank_last4 && <div><span className="text-slate-500">Bank:</span> ****{v.bank_last4} {v.ifsc && `· ${v.ifsc}`}</div>}
            {v.city && <div className="col-span-2"><span className="text-slate-500">Address:</span> {v.city}</div>}
          </div>
          {v.matched_on && v.matched_on.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {v.matched_on.map((f, i) => (
                <span key={i} className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 font-bold">
                  {FIELD_LABELS[f] || f}
                </span>
              ))}
            </div>
          )}
        </div>
        <a href={`/vendors/edit/${v.id}`} target="_blank" rel="noreferrer"
           className="text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline whitespace-nowrap"
           data-testid={`open-existing-${v.id}`}>
          Open Existing →
        </a>
      </div>
    </div>
  );
}

function DuplicateBlockedModal({ payload, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="dup-blocked-modal">
      <div className="bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-rose-200 bg-rose-50 flex items-center gap-3">
          <ShieldAlert size={24} className="text-rose-600" />
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold text-rose-900">Duplicate vendor — cannot save</h2>
            <p className="text-xs text-rose-700 mt-0.5">{payload.message}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-rose-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">
            Conflicts on: {payload.blocking_fields.map(f => FIELD_LABELS[f] || f).join(", ")}
          </div>
          {(payload.matched_vendors || []).map(v => <MatchedVendorRow key={v.id} v={v} />)}
          <div className="text-xs text-slate-500 pt-2 border-t border-slate-200">
            Bank Account + IFSC must be unique across all vendors — this is a hard block.
            Open the existing vendor record above instead of creating a duplicate.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
          <button onClick={onClose} data-testid="dup-blocked-close-btn"
                  className="bg-slate-800 hover:bg-slate-900 text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DuplicateWarningModal({ payload, canOverride, onCancel, onContinue }) {
  const [reason, setReason] = useState("");
  const riskColour = payload.risk === "high" ? "bg-rose-100 text-rose-800"
                    : payload.risk === "medium" ? "bg-amber-100 text-amber-800"
                    : "bg-yellow-100 text-yellow-800";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="dup-warning-modal">
      <div className="bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-amber-200 bg-amber-50 flex items-center gap-3">
          <AlertTriangle size={24} className="text-amber-600" />
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold text-amber-900">Possible duplicate vendor found</h2>
            <p className="text-xs text-amber-700 mt-0.5">{payload.message}</p>
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-1 font-bold ${riskColour}`}>
            {payload.risk} risk
          </span>
          <button onClick={onCancel} className="p-1 hover:bg-amber-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">
            Matches on: {payload.warning_fields.map(f => FIELD_LABELS[f] || f).join(", ")}
          </div>
          {(payload.matched_vendors || []).map(v => <MatchedVendorRow key={v.id} v={v} />)}
          {canOverride ? (
            <div className="pt-3 border-t border-slate-200 space-y-2">
              <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">
                Reason for continuing (required) <span className="text-rose-600">*</span>
              </label>
              <textarea data-testid="dup-warning-reason"
                rows={2}
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. Same company onboarded under a different procurement category with its own bank account · different legal entity with similar branding · etc."
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-[#F97316] focus:outline-none" />
            </div>
          ) : (
            <div className="pt-3 border-t border-slate-200 text-xs text-rose-700 bg-rose-50 px-3 py-2">
              You don&apos;t have permission to override the duplicate-vendor warning. Ask an admin to grant
              <strong> Vendors → Approve</strong> on your role (Administration → Users → Roles).
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onCancel} data-testid="dup-warning-cancel-btn"
                  className="bg-white border border-slate-300 px-5 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          {canOverride && (
            <button onClick={() => {
              if (!reason.trim()) { toast.error("Please provide a reason"); return; }
              onContinue(reason.trim());
            }} data-testid="dup-warning-continue-btn"
                    className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold">
              Continue Anyway
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-1.5 border-b border-slate-50 last:border-b-0">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
      <div className="col-span-2 text-sm text-slate-900">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

function VendorView({ record, onClose, onEdit }) {
  const c = record || {};
  const pocs = c.pocs || [];
  const banks = c.bank_accounts || [];
  const attachments = c.attachments || [];
  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="vendor-view-overlay">
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold">Vendor Details</div>
            <h2 className="font-display text-2xl font-bold mt-0.5">{c.name}</h2>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="view-edit-btn" onClick={onEdit} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Pencil size={14} /> Edit
            </button>
            <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={20} /></button>
          </div>
        </div>

        <div className="px-8 py-6 space-y-5 max-w-[1400px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="border border-slate-200 p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-bold">Vendor Code</div>
              <div className="text-lg font-bold mt-1">{c.vendor_code || "—"}</div>
            </div>
            <div className="border border-slate-200 p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-bold">Vendor Tag</div>
              <div className="text-lg font-bold mt-1 inline-flex items-center gap-1.5"><Tag size={14} className="text-slate-500"/> {c.skill_type || "—"}</div>
            </div>
            <div className="border border-slate-200 p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-bold">Opening Balance</div>
              <div className="text-lg font-bold mt-1 tabular">{formatINR(c.opening_balance || 0)}</div>
            </div>
            <div className="border border-slate-200 p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-bold">Bank Accounts</div>
              <div className="text-lg font-bold mt-1">{banks.length}</div>
            </div>
            <div className="border border-slate-200 p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-bold">POCs</div>
              <div className="text-lg font-bold mt-1">{pocs.length}</div>
            </div>
          </div>

          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Building2 size={14} className="text-slate-500" />
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Identity &amp; Statutory</h3>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-10">
              <Row label="Name" value={c.name} />
              <Row label="Vendor Tag" value={c.skill_type} />
              <Row label="Phone" value={c.phone} />
              <Row label="Email" value={c.email} />
              <Row label="Additional Emails" value={(c.additional_emails || []).length ? (
                <span className="flex flex-wrap gap-1.5" data-testid="vendor-view-additional-emails">
                  {(c.additional_emails || []).map((em, i) => (
                    <span key={i} className="border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] font-mono">{em}</span>
                  ))}
                </span>
              ) : ""} />
              <Row label="Primary Contact" value={c.contact_person} />
              <Row label="GST Number" value={c.gst_number && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="tabular">{c.gst_number}</span>
                  {c.gst_verified && <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-bold inline-flex items-center gap-1"><CheckCircle2 size={10}/> Verified</span>}
                </span>
              )} />
              <Row label="PAN" value={c.pan} />
              <Row label="CIN" value={c.cin} />
              <Row label="Aadhaar" value={c.aadhaar_number} />
              <Row label="License No." value={c.license_number} />
              <Row label="TDS" value={`${c.tds_section || "—"} @ ${(c.tds_rate ?? 1).toFixed(2)}%`} />
              <Row label="Payment Terms" value={c.payment_terms} />
            </div>
            {c.address && (
              <div className="px-5 pb-5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Registered Address</div>
                <div className="text-sm text-slate-800 whitespace-pre-line">{c.address}</div>
              </div>
            )}
          </Card>

          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <MapPin size={14} className="text-slate-500" />
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Billing Address</h3>
            </div>
            <div className="p-5">
              {c.billing_address ? <div className="text-sm text-slate-800 whitespace-pre-line">{c.billing_address}</div> : <div className="text-xs text-slate-400">No billing address recorded.</div>}
            </div>
          </Card>

          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Landmark size={14} className="text-slate-500" />
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Bank Accounts ({banks.length})</h3>
            </div>
            <div className="p-5">
              {banks.length === 0 ? <div className="text-xs text-slate-400">No bank accounts on file.</div> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b-2 border-slate-200">
                    <tr>{["Label", "Holder", "Bank", "Account No.", "IFSC", "Branch", "Type", "UPI"].map(h => (
                      <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {banks.map((b, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-2 px-3 font-bold">
                          {b.label || "—"}
                          {b.is_default && <span className="ml-2 text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-bold">Default</span>}
                        </td>
                        <td className="py-2 px-3 text-slate-700">{b.account_holder_name || "—"}</td>
                        <td className="py-2 px-3 text-slate-700">{b.bank_name || "—"}</td>
                        <td className="py-2 px-3 text-slate-700 tabular">{b.account_number || "—"}</td>
                        <td className="py-2 px-3 text-slate-700 tabular">{b.ifsc_code || "—"}</td>
                        <td className="py-2 px-3 text-slate-700">{b.branch || "—"}</td>
                        <td className="py-2 px-3 text-slate-700">{b.account_type || "—"}</td>
                        <td className="py-2 px-3 text-slate-700">{b.upi_id || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>

          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <UserCheck size={14} className="text-slate-500" />
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Points of Contact ({pocs.length})</h3>
            </div>
            <div className="p-5">
              {pocs.length === 0 ? <div className="text-xs text-slate-400">No POCs on file.</div> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b-2 border-slate-200">
                    <tr>{["Name", "Designation", "Phone", "Email", "Notes"].map(h => (
                      <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {pocs.map((p, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-2 px-3 font-bold">{p.name || "—"}</td>
                        <td className="py-2 px-3 text-slate-700">{p.designation || "—"}</td>
                        <td className="py-2 px-3 text-slate-700">{p.phone ? <span className="inline-flex items-center gap-1"><Phone size={11}/>{p.phone}</span> : "—"}</td>
                        <td className="py-2 px-3 text-slate-700">{p.email ? <span className="inline-flex items-center gap-1"><Mail size={11}/>{p.email}</span> : "—"}</td>
                        <td className="py-2 px-3 text-slate-500 text-xs">{p.notes || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>

          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Paperclip size={14} className="text-slate-500" />
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Documents / Attachments ({attachments.length})</h3>
            </div>
            <div className="p-5">
              {attachments.length === 0 ? <div className="text-xs text-slate-400">No attachments on file.</div> : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {attachments.map((a, i) => {
                    const isPdf = (a.mime_type || "").includes("pdf") || (a.filename || "").toLowerCase().endsWith(".pdf");
                    return (
                      <a key={a.id || i} href={backendUrl(a.url)} target="_blank" rel="noreferrer" className="border border-slate-200 px-4 py-3 hover:border-[#F97316] hover:bg-orange-50/30 flex items-center gap-3" data-testid={`view-attach-${i}`}>
                        <div className={`w-9 h-9 flex items-center justify-center ${isPdf ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"}`}>
                          {isPdf ? <FileText size={16} /> : <ImageIcon size={16} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold truncate">{a.filename}</div>
                          <div className="text-[11px] text-slate-500 tabular">{fmtBytes(a.size)}</div>
                        </div>
                        <Download size={14} className="text-slate-500" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>
    </div>
  );
}
